/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Vu Dinh Luan (vdluan@rsupartners.com)
 *
 * @description
 *
 * @created at Mon Dec 18 2024
 **/

import { Typography, Row, Column, Icon, Tooltip } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";
import { usePhoneCall } from "@/frameworks/core/hooks";
import { ChronoUtils, Configure } from "@/frameworks/libraries";
import { colors } from "@/frameworks/core/styles";

import { S } from "../..";

interface VoiceNoResponseSystemChatProps {
	variant: ChatUI.Size;
	createAt?: number;
	caller?: {
		id: string;
		name: string;
	};
	receiver?: {
		id: string;
		name: string;
	};
	callUsers?: [
		{
			id: string;
			name: string;
			profileImagePath: string;
			colorProfile: string;
		}
	];
}

const VoiceNoResponseSystemChat = ({
	variant,
	createAt,
	caller,
	receiver,
	callUsers
}: VoiceNoResponseSystemChatProps) => {
	const phoneCall = usePhoneCall();

	const isCallStartedFromMe = caller?.id === Configure.userID;
	const isGroupCall = callUsers && callUsers.length > 2;

	return (
		<S.SystemChatRow
			css={{
				borderRadius: "12px",
				padding: "8px 16px",
				backgroundColor: "#fff"
			}}
		>
			<Column css={{ width: "100%" }}>
				<S.ChatContent variant={variant}>
					<Row gap={10} verticalAlign="center">
						<S.IconMissedCallWrapper>
							<Icon name="CallMissed24" size={16} fill={colors.error_60_100} />
						</S.IconMissedCallWrapper>
						<Typography typoSize="Title2">{_T("부재중 통화")}</Typography>
					</Row>
					<Typography typoSize="Body4" typoColor="neutral_60_100">
						{ChronoUtils.formatTimeChatTime(createAt || 0)}
					</Typography>
				</S.ChatContent>
				<S.ChatContent variant={variant} css={{ paddingLeft: 40 }}>
					<Column>
						<Typography typoSize="Body4">{_T("부재중 전화가 수신되었습니다.")}</Typography>
					</Column>
				</S.ChatContent>
				<div css={{ padding: "10px 0px 10px 40px", width: "280px", boxSizing: "content-box" }}>
					<Row
						gap={4}
						css={{
							border: `1px solid ${colors.neutral_20_100}`,
							borderRadius: 4,
							padding: "6px 10px",
							width: "100%"
						}}
					>
						{isGroupCall ? (
							<Tooltip
								message={_T("그룹전화: {{usersName}}", {
									usersName: callUsers
										?.map((user) => user.name)
										.filter(Boolean)
										.join(", ")
								})}
								hasIcon={false}
							>
								<Column
									css={{
										maxWidth: "75%",
										whiteSpace: "normal",
										overflow: "hidden",
										textOverflow: "ellipsis",
										display: "-webkit-box",
										WebkitLineClamp: 2,
										WebkitBoxOrient: "vertical"
									}}
								>
									<Typography typoSize="Body3_MD">
										{_T("그룹전화: {{usersName}}", {
											usersName: callUsers
												.map((user) => user.name)
												.filter(Boolean)
												.join(", ")
										})}
									</Typography>
								</Column>
							</Tooltip>
						) : (
							<Column
								css={{
									maxWidth: "75%"
								}}
							>
								<Typography typoSize="Body3_MD">{_T("1:1 전화")}</Typography>
							</Column>
						)}
						<S.CallButton
							onClick={async () => {
								const callID = await phoneCall.start_dial({
									type: "normal",
									callerIDs: [isCallStartedFromMe ? receiver?.id || "" : caller?.id || ""]
								});

								phoneCall.popup.show({
									callID,
									callerID: isCallStartedFromMe ? receiver?.id || "" : caller?.id || "",
									uiType: "floating"
								});
							}}
						>
							<Icon name="Call0124" size={20} fill={colors.neutral_00_100} />
						</S.CallButton>
					</Row>
				</div>
			</Column>
		</S.SystemChatRow>
	);
};

export default VoiceNoResponseSystemChat;
